import React, { useState, useEffect } from 'react';
import { API } from '../../services/api';
import './InvoicesSection.css';

function InvoicesSection({ organizationId }) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [invoices, setInvoices] = useState([]);
    const [billingPeriods, setBillingPeriods] = useState([]);
    const [activeView, setActiveView] = useState('invoices'); // 'invoices' ou 'periods'

    useEffect(() => {
        loadData();
    }, [organizationId]);

    const loadData = async () => {
        setLoading(true);
        setError('');
        try {
            const [invoicesRes, periodsRes] = await Promise.all([
                API.getInvoices(),
                API.getBillingPeriods()
            ]);

            if (invoicesRes?.data?.invoices) {
                setInvoices(invoicesRes.data.invoices);
            }
            if (periodsRes?.data?.billing_periods) {
                setBillingPeriods(periodsRes.data.billing_periods);
            }
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const date = new Date(dateStr);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatPeriod = (year, month) => {
        const monthNames = [
            'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
            'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'
        ];
        return `${monthNames[month - 1]} ${year}`;
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'EUR'
        }).format(amount);
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            draft: { label: 'Brouillon', className: 'status-draft' },
            sent: { label: 'Envoyee', className: 'status-sent' },
            paid: { label: 'Payee', className: 'status-paid' },
            cancelled: { label: 'Annulee', className: 'status-cancelled' },
            pending: { label: 'En attente', className: 'status-pending' },
            calculated: { label: 'Calculee', className: 'status-calculated' },
            invoiced: { label: 'Facturee', className: 'status-invoiced' }
        };
        const config = statusConfig[status] || { label: status, className: '' };
        return <span className={`status-badge ${config.className}`}>{config.label}</span>;
    };

    const handleDownloadPdf = async (invoiceId) => {
        try {
            // Ouvrir dans un nouvel onglet
            const token = localStorage.getItem('access_token');
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoiceId}/pdf`;

            // Creer un lien temporaire pour le telechargement
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Erreur lors du telechargement');
            }

            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;

            // Determiner le nom du fichier depuis le header ou utiliser un defaut
            const contentDisposition = response.headers.get('Content-Disposition');
            let filename = 'facture.pdf';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="?(.+)"?/);
                if (match) filename = match[1];
            }

            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Download error:', error);
            setError('Erreur lors du telechargement de la facture');
        }
    };

    const handlePreview = (invoiceId) => {
        const token = localStorage.getItem('access_token');
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoiceId}/preview`;
        window.open(url + `?token=${token}`, '_blank');
    };

    if (loading) {
        return <div className="invoices-section loading">Chargement...</div>;
    }

    return (
        <div className="invoices-section">
            {error && <div className="message error">{error}</div>}

            <div className="section-header">
                <div className="view-toggle">
                    <button
                        className={`toggle-btn ${activeView === 'invoices' ? 'active' : ''}`}
                        onClick={() => setActiveView('invoices')}
                    >
                        Factures
                    </button>
                    <button
                        className={`toggle-btn ${activeView === 'periods' ? 'active' : ''}`}
                        onClick={() => setActiveView('periods')}
                    >
                        Historique d'usage
                    </button>
                </div>
            </div>

            {activeView === 'invoices' && (
                <div className="invoices-list">
                    {invoices.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h3>Aucune facture</h3>
                            <p>Vos factures apparaitront ici une fois generees.</p>
                        </div>
                    ) : (
                        <table className="invoices-table">
                            <thead>
                                <tr>
                                    <th>Numero</th>
                                    <th>Date</th>
                                    <th>Periode</th>
                                    <th>Montant HT</th>
                                    <th>TVA</th>
                                    <th>Montant TTC</th>
                                    <th>Statut</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.map((invoice) => (
                                    <tr key={invoice.id}>
                                        <td className="invoice-number">{invoice.invoice_number}</td>
                                        <td>{formatDate(invoice.invoice_date)}</td>
                                        <td>
                                            {invoice.period_start && invoice.period_end
                                                ? `${formatDate(invoice.period_start)} - ${formatDate(invoice.period_end)}`
                                                : '-'
                                            }
                                        </td>
                                        <td className="amount">{formatAmount(invoice.total_ht)}</td>
                                        <td className="amount">{formatAmount(invoice.vat_amount)}</td>
                                        <td className="amount total">{formatAmount(invoice.total_ttc)}</td>
                                        <td>{getStatusBadge(invoice.status)}</td>
                                        <td className="actions">
                                            <button
                                                className="btn-icon"
                                                onClick={() => handleDownloadPdf(invoice.id)}
                                                title="Telecharger PDF"
                                            >
                                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {activeView === 'periods' && (
                <div className="periods-list">
                    {billingPeriods.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">
                                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                            </div>
                            <h3>Aucun historique</h3>
                            <p>L'historique de vos usages apparaitra ici.</p>
                        </div>
                    ) : (
                        <table className="periods-table">
                            <thead>
                                <tr>
                                    <th>Periode</th>
                                    <th>Utilisateurs</th>
                                    <th>Boites partagees</th>
                                    <th>Operateurs</th>
                                    <th>Total facturable</th>
                                    <th>Montant HT</th>
                                    <th>Statut</th>
                                    <th>Facture</th>
                                </tr>
                            </thead>
                            <tbody>
                                {billingPeriods.map((period) => (
                                    <tr key={period.id}>
                                        <td className="period-name">
                                            {formatPeriod(period.period_year, period.period_month)}
                                        </td>
                                        <td className="number">{period.regular_users_count}</td>
                                        <td className="number">{period.shared_mailboxes_count}</td>
                                        <td className="number">{period.operators_count}</td>
                                        <td className="number highlight">{period.total_billable_units}</td>
                                        <td className="amount">{formatAmount(period.total_amount_ht)}</td>
                                        <td>{getStatusBadge(period.status)}</td>
                                        <td>
                                            {period.invoice_number ? (
                                                <span className="invoice-ref">{period.invoice_number}</span>
                                            ) : (
                                                <span className="no-invoice">-</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}

export default InvoicesSection;
